import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TOXBOX_API_BASE } from "./toxboxConfig";
import { BASE_APIURL } from "../../utils/helpers";
import { Search, X } from "lucide-react";
import styles from "./Blog.module.css";

const stripHtml = (html) => {
  if (!html) return "";
  // 1. Remove style blocks and their internal CSS content completely
  let clean = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
  // 2. Remove script blocks and their javascript content completely
  clean = clean.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
  // 3. Strip all other HTML tags
  clean = clean.replace(/<[^>]+>/g, "");
  // 4. Normalize multiple whitespace characters
  clean = clean.replace(/\s+/g, " ");
  return clean.trim();
};


// Helper — Format ISO date
function formatDate(isoString) {
  if (!isoString) return "";
  try {
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// Simple slugify helper
function slugify(text) {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");
}

// Skeleton card loader
function SkeletonCard() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={styles.skeletonThumb} />
      <div className={styles.skeletonBody}>
        <div className={styles.skeletonPill} />
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLine} style={{ width: "70%" }} />
        <div className={styles.skeletonMeta} />
      </div>
    </div>
  );
}

// Unified Blog Card (No Source Badge)
function BlogCard({ blog, onClick }) {
  return (
    <article
      className={styles.blogCard}
      onClick={() => onClick(blog)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(blog)}
      aria-label={`Read article: ${blog.title}`}
    >
      <div className={styles.cardThumb}>
        {blog.thumbnail ? (
          <img
            src={blog.thumbnail}
            alt={blog.title}
            className={styles.thumbImg}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling?.style?.removeProperty("display");
            }}
          />
        ) : null}
        <div
          className={styles.thumbFallback}
          style={blog.thumbnail ? { display: "none" } : {}}
        >
          <span className={styles.thumbFallbackIcon}>📝</span>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
          {blog.category && (
            <span className={styles.industryPill} style={{ background: blog.isExternal ? "rgba(56, 189, 248, 0.15)" : "rgba(192, 132, 252, 0.15)", color: blog.isExternal ? "#38bdf8" : "#c084fc", border: blog.isExternal ? "1px solid rgba(56, 189, 248, 0.3)" : "1px solid rgba(192, 132, 252, 0.3)" }}>
              {blog.category}
            </span>
          )}
        </div>

        <h3 className={styles.cardTitle}>{blog.title}</h3>

        <p className={styles.cardDesc}>
          {blog.summary || "Read the full article for insights and tips."}
        </p>

        <div className={styles.cardMeta}>
          <span>{formatDate(blog.date)}</span>
        </div>
      </div>
    </article>
  );
}

export default function BlogListPage() {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);

  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Paginated states
  const [toxboxPage, setToxboxPage] = useState(1);
  const [insightsToken, setInsightsToken] = useState(null);
  const [hasMoreToxbox, setHasMoreToxbox] = useState(true);
  const [hasMoreInsights, setHasMoreInsights] = useState(true);

  const observerRef = useRef();
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Initialize Filters dynamically from backends on mount
  useEffect(() => {
    const initializeFilters = async () => {
      // 1. Fetch categories from Provaantech Backend
      try {
        const res = await fetch(`${BASE_APIURL}/api/blogs/categories`);
        if (res.ok) {
          const resJson = await res.json();
          if (Array.isArray(resJson.categories)) {
            setCategories(resJson.categories);
          }
        }
      } catch (e) {
        console.error("Failed to fetch categories:", e);
      }

      // 2. Fetch industries from ToxBox Backend
      try {
        const res = await fetch(`${TOXBOX_API_BASE}?page=1&limit=1`);
        if (res.ok) {
          const resJson = await res.json();
          const inds = resJson.metadata?.availableFilters?.industries || [];
          if (inds.length > 0) {
            setIndustries(inds);
          }
        }
      } catch (e) {
        console.error("Failed to fetch industries:", e);
      }
    };
    initializeFilters();
  }, []);

  // Fetch and Merge Blogs in Parallel (Non-blocking)
  const fetchBlogs = useCallback(async (filterVal = "All", isLoadMore = false) => {
    if (isLoadMore && isFetchingRef.current) return;

    // Abort any ongoing fetch requests to prevent race conditions or duplicate loads
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    isFetchingRef.current = true;
    setLoading(true);
    setError("");

    try {
      const filterLower = filterVal.toLowerCase();

      // Check which APIs to call based on case-insensitive filter match
      const matchedCategory = categories.find(cat => cat.toLowerCase() === filterLower);
      const matchedIndustry = industries.find(ind => ind.toLowerCase() === filterLower);

      const fetchFromExternal = filterVal === "All" || !!matchedIndustry;
      const fetchFromInsights = filterVal === "All" || !!matchedCategory;

      const currentToxboxPage = isLoadMore ? toxboxPage : 1;
      const currentInsightsToken = isLoadMore ? insightsToken : null;

      const promises = [];

      // 1. ToxBox BE Fetch Promise (Page Pagination)
      if (fetchFromExternal && (isLoadMore ? hasMoreToxbox : true)) {
        let url = `${TOXBOX_API_BASE}?page=${currentToxboxPage}&limit=9`;
        if (filterVal !== "All" && matchedIndustry) {
          url += `&industry=${encodeURIComponent(matchedIndustry)}`;
        }
        promises.push(
          fetch(url, { signal: controller.signal })
            .then(res => res.ok ? res.json() : null)
            .then(data => ({ type: "toxbox", data }))
            .catch(e => {
              console.error("ToxBox fetch error:", e);
              return { type: "toxbox", data: null };
            })
        );
      } else {
        promises.push(Promise.resolve({ type: "toxbox", data: null }));
      }

      // 2. Provaantech BE Fetch Promise (Continuation Cursor Pagination)
      if (fetchFromInsights && (isLoadMore ? hasMoreInsights : true)) {
        let url = `${BASE_APIURL}/api/blogs/all-posts/short-summary?limit=9`;
        if (filterVal !== "All" && matchedCategory) {
          url = `${BASE_APIURL}/api/blogs/posts/category-summary/${encodeURIComponent(matchedCategory)}?limit=9`;
        }
        if (currentInsightsToken) {
          url += `&continuationToken=${encodeURIComponent(currentInsightsToken)}`;
        }
        promises.push(
          fetch(url, { signal: controller.signal })
            .then(res => res.ok ? res.json() : null)
            .then(data => ({ type: "insights", data }))
            .catch(e => {
              console.error("Insights fetch error:", e);
              return { type: "insights", data: null };
            })
        );
      } else {
        promises.push(Promise.resolve({ type: "insights", data: null }));
      }

      // Resolve concurrently
      const results = await Promise.all(promises);

      let newExternalData = [];
      let newInsightsData = [];

      results.forEach(result => {
        if (!result) return;
        if (result.type === "toxbox" && result.data) {
          newExternalData = result.data.data || [];
          const pagination = result.data.pagination;
          const hasMore = pagination ? pagination.page < pagination.totalPages : false;
          setHasMoreToxbox(hasMore);
          if (hasMore) {
            setToxboxPage(currentToxboxPage + 1);
          } else {
            setHasMoreToxbox(false);
          }
        } else if (result.type === "insights" && result.data) {
          newInsightsData = result.data.posts || [];
          const nextToken = result.data.nextContinuationToken || null;
          setInsightsToken(nextToken);
          setHasMoreInsights(Boolean(nextToken));
        }
      });

      // Update local hasMore flags if we are resetting search filters
      if (!isLoadMore) {
        if (!fetchFromExternal) setHasMoreToxbox(false);
        if (!fetchFromInsights) setHasMoreInsights(false);
      }

      // Map to unified layout format
      const mappedExternal = newExternalData.map((b) => ({
        _id: b._id || b.id,
        id: b.id || b._id,
        title: b.title,
        summary: b.metaDescription || (b.htmlContent ? stripHtml(b.htmlContent).substring(0, 160) + "..." : "") || "Read the full article for insights and tips.",
        thumbnail: b.thumbnailUrl || "https://via.placeholder.com/400x200?text=No+Image",
        category: b.industry || "External Blog",
        date: b.publishedAt || b.createdAt,
        isExternal: true,
        slug: b.slug,
        raw: b
      }));

      const mappedInsights = newInsightsData.map((b) => ({
        _id: b.key || b.id || b._id,
        id: b.id || b._id,
        title: b.title,
        summary: b.summary || (b.content ? stripHtml(b.content).substring(0, 160) + "..." : ""),
        thumbnail: b.thumbnail || "https://via.placeholder.com/400x200?text=No+Image",
        category: b.category || "Insight",
        date: b.createdAt,
        isExternal: false,
        slug: `${slugify(b.title)}-${b.id}`,
        raw: b
      }));

      const combined = [...mappedExternal, ...mappedInsights];
      combined.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });

      if (isLoadMore) {
        setBlogs(prev => [...prev, ...combined]);
      } else {
        setBlogs(combined);
      }

    } catch (err) {
      if (err.name === "AbortError") {
        return; // Request was canceled intentionally — ignore
      }
      console.error("[BlogListPage] Fetch error:", err);
      setError("Failed to load articles. Please check your connection.");
    } finally {
      if (abortControllerRef.current === controller) {
        isFetchingRef.current = false;
        setLoading(false);
      }
    }
  }, [categories, industries, toxboxPage, insightsToken, hasMoreToxbox, hasMoreInsights]);

  // Initial fetch triggers exactly once on filter change
  useEffect(() => {
    setToxboxPage(1);
    setInsightsToken(null);
    setHasMoreToxbox(true);
    setHasMoreInsights(true);
    setBlogs([]);
    fetchBlogs(selectedFilter, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  // Navigate to appropriate detail view
  const handleCardClick = (blog) => {
    if (blog.isExternal) {
      navigate(`/blog/${blog.slug}`, { state: { blog: blog.raw } });
    } else {
      navigate(`/insights/${blog.slug}`, { state: { id: blog.id, blog: blog.raw } });
    }
  };

  // Handle filter changes
  const handleFilterSelect = (filter) => {
    const nextFilter = filter === selectedFilter ? "All" : filter;
    setSelectedFilter(nextFilter);
  };

  // Local client-side filtering for search query
  const filteredBlogs = blogs.filter((blog) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const titleMatch = blog.title.toLowerCase().includes(query);
    const descMatch = (blog.summary || "").toLowerCase().includes(query);
    const catMatch = (blog.category || "").toLowerCase().includes(query);
    return titleMatch || descMatch || catMatch;
  });

  // Infinite Scroll Trigger
  const hasMore = hasMoreToxbox || hasMoreInsights;
  const lastBlogRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      if (node) {
        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            fetchBlogs(selectedFilter, true);
          }
        });
        observerRef.current.observe(node);
      }
    },
    [loading, hasMore, selectedFilter, fetchBlogs]
  );

  // Clean up observer when component unmounts
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const renderSkeletons = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <SkeletonCard key={i} />
    ));

  const renderError = () => (
    <div className={styles.errorState}>
      <div className={styles.errorIcon}>⚠️</div>
      <h3 className={styles.errorTitle}>Couldn't load articles</h3>
      <p className={styles.errorMessage}>{error}</p>
      <button
        className={styles.retryBtn}
        onClick={() => fetchBlogs(selectedFilter, false)}
      >
        Try Again
      </button>
    </div>
  );

  const renderEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>📭</div>
      <h3 className={styles.emptyTitle}>No articles found</h3>
      <p className={styles.emptyMessage}>
        {selectedFilter !== "All"
          ? `No published articles in the "${selectedFilter}" category yet.`
          : "No published articles available yet. Check back soon!"}
      </p>
      {selectedFilter !== "All" && (
        <button
          className={styles.retryBtn}
          onClick={() => handleFilterSelect("All")}
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  const renderSearchEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>🔍</div>
      <h3 className={styles.emptyTitle}>No matching articles</h3>
      <p className={styles.emptyMessage}>
        We couldn't find any articles matching "{searchQuery}". Try adjusting your keywords.
      </p>
      <button
        className={styles.retryBtn}
        onClick={() => setSearchQuery("")}
      >
        Clear Search
      </button>
    </div>
  );

  // Group and Deduplicate categories and industries case-insensitively
  const allFilters = React.useMemo(() => {
    const seen = new Set();
    const uniqueFilters = [];
    
    const cleanName = (name) => name ? name.trim().toLowerCase() : "";
    const toDisplayCase = (name) => {
      if (!name) return "";
      return name
        .trim()
        .split(/\s+/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    [...categories, ...industries].forEach(item => {
      const clean = cleanName(item);
      if (clean && !seen.has(clean)) {
        seen.add(clean);
        uniqueFilters.push(toDisplayCase(item));
      }
    });

    // Sort alphabetically so the order is static and predictable
    uniqueFilters.sort((a, b) => a.localeCompare(b));

    return ["All", ...uniqueFilters];
  }, [categories, industries]);

  return (
    <div className={styles.pageWrapper}>
      <header className={styles.pageHeader}>
        <p className={styles.headerEyebrow}>Knowledge Hub</p>
        <h1 className={styles.pageTitle}>Insights &amp; Resources</h1>
        <p className={styles.pageSubtitle}>
          Deep-dives, case studies, engineering findings, and recruiting insights from the Provaantech teams.
        </p>
      </header>

      <div className={styles.contentContainer}>
        {!error && (
          <div className={styles.controlsRow} style={{ flexDirection: "column", gap: "20px", alignItems: "stretch" }}>
            {/* Search Bar */}
            <div className={styles.searchWrapper} style={{ maxWidth: "500px", margin: "0 auto w-full" }}>
              <span className={styles.searchIcon}>
                <Search size={18} />
              </span>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className={styles.clearSearchBtn}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Filter pills list (uses .filterRow styles from CSS module for horizontal scroll) */}
            <div className={styles.filterRow} role="group" aria-label="Filter by category">
              {allFilters.map((filter) => (
                <button
                  key={filter}
                  className={`${styles.filterPill} ${selectedFilter === filter ? styles.filterPillActive : ""}`}
                  onClick={() => handleFilterSelect(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        )}

        {!loading && !error && filteredBlogs.length > 0 && (
          <p className={styles.resultCount} style={{ textAlign: "center", marginBottom: "24px" }}>
            {searchQuery
              ? `Showing ${filteredBlogs.length} matching result${filteredBlogs.length === 1 ? "" : "s"}`
              : selectedFilter !== "All"
              ? `Showing ${filteredBlogs.length} articles in "${selectedFilter}"`
              : `Showing ${filteredBlogs.length} articles`}
          </p>
        )}

        {/* Grid View */}
        {blogs.length === 0 && loading ? (
          <div className={styles.blogGrid}>{renderSkeletons()}</div>
        ) : error ? (
          renderError()
        ) : blogs.length === 0 ? (
          renderEmpty()
        ) : filteredBlogs.length === 0 ? (
          renderSearchEmpty()
        ) : (
          <>
            <div className={styles.blogGrid}>
              {filteredBlogs.map((blog, idx) => {
                const isLast = filteredBlogs.length === idx + 1;
                return (
                  <div 
                    key={blog._id} 
                    ref={isLast ? lastBlogRef : null}
                  >
                    <BlogCard blog={blog} onClick={handleCardClick} />
                  </div>
                );
              })}
            </div>

            {/* Bottom loading spinner for scrolling pagination */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "center", padding: "32px 0", width: "100%" }}>
                <style>{`
                  @keyframes spin-scroll-loader {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", border: "3px solid rgba(59, 130, 246, 0.15)", borderTopColor: "#3b82f6", animation: "spin-scroll-loader 0.8s linear infinite" }} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
